<script setup lang="ts">
import { computed, ref, watch, type CSSProperties, type PropType } from 'vue';
import type { SelectOption } from 'naive-ui';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaPanel from '@/components/design/composite/GaiaPanel.vue';
import { IconClose } from '@/components/design/icons';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { gaiaNeutral, gaiaShellPadding } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard';
import { logger } from '@/utils/logger';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaRecipientSelect from '@/components/design/domain/people/GaiaRecipientSelect.vue';
import { ACCESS_LEVEL_OPTIONS, ACCESS_PERMISSION_READ } from '@/utils/accessPermission';
import EntityFieldInput from './EntityFieldInput.vue';
import {
  allFieldsOf,
  entityLabel,
  isFieldShown,
  rowValues,
  toText,
  updateEntity,
  validateValues,
  type EntityField,
  type EntityFormValues,
  type EntityListConfig,
  type EntityRow,
} from './entityListConfig';

/**
 * The entity create / edit drawer, driven by an `EntityListConfig`'s fields.
 * Lives apart from the list so the detail page edits through the same form —
 * the one place that knows about `showWhen`, `loadOptions` and the
 * collapsible group. `row` null means create.
 */
const props = defineProps({
  show: { type: Boolean, required: true },
  config: { type: Object as PropType<EntityListConfig>, required: true },
  row: { type: Object as PropType<EntityRow | null>, default: null },
  /** Name of the record being edited — the dialog title can't recompose it. */
  rowName: { type: String, default: '' },
  /** Create-mode starting values over the fields' blanks (a preset kind, a link). */
  defaults: { type: Object as PropType<EntityFormValues>, default: () => ({}) },
  /**
   * Create-mode fields the context has already decided — shown, not editable.
   * A representative added for a company is a natural person who represents
   * that company; letting either change would break what the caller opened for.
   */
  lockedKeys: { type: Array as PropType<string[]>, default: () => [] },
  /**
   * Labels for a picker's `defaults` (a preset link's uid → the party's name),
   * which no search has returned yet — without them the chip reads as a uid.
   */
  defaultOptions: {
    type: Object as PropType<Record<string, SelectOption[]>>,
    default: () => ({}),
  },
  /**
   * Render as a side panel for Layout's `#drawer` slot instead of an overlay
   * drawer: no mask, and the page beside it stays usable. The parent opens the
   * slot (Layout's `drawer-open`) off the same `show`.
   */
  inline: { type: Boolean, default: false },
  /**
   * What the record is called in the title and submit button, over the
   * config's own name — a party created as a preset kind reads as that kind
   * ("Create legal entity"), not the generic "contractual party".
   */
  label: { type: String, default: '' },
});

/** `saved` carries the created record when the config's `create` returns one. */
const emit = defineEmits<{ 'update:show': [value: boolean]; saved: [row?: EntityRow] }>();

const message = useGaiaToast();

const showModel = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const label = computed(() => props.label || entityLabel(props.config));

/**
 * Mode is its own ref rather than read off `row`: the dialog keeps rendering
 * through its fade-out, and a mode that flips mid-animation would swap every
 * label to the other branch on the way out.
 */
const mode = ref<'create' | 'edit'>('create');
/** A `tags` / `multiselect` field holds an array; everything else holds a string. */
const formValues = ref<EntityFormValues>({});
/** The collapsible name-parts group is open. */
const moreFieldsOpen = ref(false);
const formErrors = ref<Record<string, string>>({});
const saving = ref(false);
const saveError = ref<string | null>(null);

const allFields = computed<EntityField[]>(() => allFieldsOf(props.config));

function isShown(field: EntityField): boolean {
  // A list the row didn't load (a person's companies, off the detail page) has
  // nothing to show — and the save leaves it alone.
  if (
    mode.value === 'edit' &&
    field.kind === 'multiselect' &&
    !Array.isArray(props.row?.[field.key])
  )
    return false;
  if (mode.value === 'create' && field.editOnly) return false;
  return isFieldShown(field, formValues.value);
}

/** What an untouched field starts as: a select takes its first option. */
function blankValue(field: EntityField): string | string[] {
  if (field.kind === 'tags' || field.kind === 'multiselect') return [];
  if (field.kind === 'select') return toText(field.options?.[0]?.value);
  return '';
}

/** Seed from the row (edit) or the fields' blanks (create), each time it opens. */
// ── Access: share a new record in the same save ─────────────────────────────
// Without it, granting a colleague access meant creating the record, opening
// its page, and sharing from its Sharing tab. Create-only: an existing
// record's access lives on that tab, with the shares it already has.
const canShare = computed(() => mode.value === 'create' && Boolean(props.config.api.share));
const shareOpen = ref(false);
const shareUserUids = ref<string[]>([]);
const shareRoleUids = ref<string[]>([]);
const shareLevel = ref<number>(ACCESS_PERMISSION_READ);
const shareCount = computed(() => shareUserUids.value.length + shareRoleUids.value.length);

function resetShare(): void {
  shareOpen.value = false;
  shareUserUids.value = [];
  shareRoleUids.value = [];
  shareLevel.value = ACCESS_PERMISSION_READ;
}

/**
 * Share the record just created. A failure here must not lose the record: it
 * exists already, so the user is told to finish from its Sharing tab.
 */
async function shareCreated(created: EntityRow): Promise<void> {
  const share = props.config.api.share;
  if (!share || shareCount.value === 0) {
    message.success(`${label.value} created successfully`);
    return;
  }
  try {
    await share(created.uid, {
      userUids: shareUserUids.value,
      roleUids: shareRoleUids.value,
      permissions: shareLevel.value,
    });
    message.success(
      `${label.value} created and shared with ${shareCount.value} ${shareCount.value === 1 ? 'recipient' : 'recipients'}`
    );
  } catch (error) {
    logger.error('Error sharing created record:', error);
    message.warning(
      `${label.value} created, but sharing failed: ${error instanceof Error ? error.message : 'unknown error'}. Share it from its Sharing tab.`
    );
  }
}

/** The form as it opened — what "unsaved" is measured against. */
const openedValues = ref('');

/** The open form differs from how it opened, or has recipients picked to share with. */
const dirty = computed(
  () =>
    props.show &&
    !saving.value &&
    (JSON.stringify(formValues.value) !== openedValues.value || shareCount.value > 0)
);

useUnsavedChangesGuard(() => dirty.value, `Your ${label.value.toLowerCase()} changes`);

function initialize(): void {
  const row = props.row;
  mode.value = row ? 'edit' : 'create';
  formErrors.value = {};
  saveError.value = null;
  resetShare();
  formValues.value = row
    ? rowValues(props.config, row)
    : {
        ...Object.fromEntries(allFields.value.map(field => [field.key, blankValue(field)])),
        ...props.defaults,
      };
  openedValues.value = JSON.stringify(formValues.value);
  // Open the collapsed group when the record already uses any of its fields —
  // otherwise an edit would silently hide a value the record actually has.
  moreFieldsOpen.value = row
    ? (props.config.collapsible?.fields ?? []).some(field => toText(row[field.key]).trim())
    : false;
}

// The row is watched too: beside a live list, another row's Edit can be
// pressed while the panel is already open.
watch(
  () => [props.show, props.row] as const,
  ([open]) => {
    if (open) initialize();
  },
  { immediate: true }
);

function onFieldInput(field: EntityField, value: string | string[]): void {
  formValues.value = { ...formValues.value, [field.key]: value };
  if (formErrors.value[field.key]) {
    formErrors.value = { ...formErrors.value, [field.key]: '' };
  }
}

/**
 * The dialog body as a flat list, the way the mock builds it: field rows, and
 * — where a config declares one — a "More …" toggle, the tinted box it opens,
 * and the rule that closes the group off.
 */
type FormGroup =
  | { kind: 'row'; fields: EntityField[]; grid: string; tinted?: boolean }
  | { kind: 'toggle' }
  | { kind: 'divider' };

/** Each form row is its own grid so a 2-field row reads as two equal halves. */
function rowGrid(fields: EntityField[]): string {
  return fields.map(field => field.width ?? '1fr').join(' ');
}

const formGroups = computed<FormGroup[]>(() => {
  const groups: FormGroup[] = [];
  const extra = props.config.collapsible;
  for (const [index, allInRow] of props.config.fields.entries()) {
    const fieldRow = allInRow.filter(field => isShown(field));
    if (fieldRow.length) groups.push({ kind: 'row', fields: fieldRow, grid: rowGrid(fieldRow) });
    // A party's name parts only apply to people, so the group hides with them.
    const extraFields = extra?.fields.filter(field => isShown(field)) ?? [];
    if (extra && index === extra.afterRow && extraFields.length) {
      groups.push({ kind: 'toggle' });
      if (moreFieldsOpen.value) {
        groups.push({ kind: 'row', fields: extraFields, grid: '1fr 1fr', tinted: true });
      }
      groups.push({ kind: 'divider' });
    }
  }
  return groups;
});

async function saveForm(): Promise<void> {
  const errors = validateValues(props.config, formValues.value);
  formErrors.value = errors;
  if (Object.keys(errors).length) return;

  saving.value = true;
  saveError.value = null;
  try {
    let created: EntityRow | undefined;
    if (props.row) {
      await updateEntity(props.config, props.row.uid, { ...formValues.value }, props.row);
      message.success(`${label.value} updated successfully`);
    } else {
      created = await props.config.api.create({ ...formValues.value });
      if (created) await shareCreated(created);
      else message.success(`${label.value} created successfully`);
    }
    showModel.value = false;
    emit('saved', created);
  } catch (error) {
    logger.error('Error saving record:', error);
    saveError.value =
      error instanceof Error ? error.message : `Failed to save ${label.value.toLowerCase()}`;
  } finally {
    saving.value = false;
  }
}

function formatDateTime(raw: unknown): string {
  const value = toText(raw);
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const formTitle = computed(() =>
  mode.value === 'create' ? `Create ${label.value.toLowerCase()}` : `Edit “${props.rowName}”`
);

const formSubtitle = computed(() =>
  mode.value === 'create'
    ? 'Referenced by contracts, reviews and templates once saved.'
    : `Created ${formatDateTime(props.row?.created_at)}`
);

const formFootNote = computed(() =>
  mode.value === 'edit' && props.row?.updated_at
    ? `Last saved ${formatDateTime(props.row.updated_at)}`
    : ''
);

const submitLabel = computed(() => {
  if (saving.value) return 'Saving…';
  // The side panel's title already says what is being created or edited.
  if (props.inline) return 'Save';
  return mode.value === 'create' ? `Create ${label.value.toLowerCase()}` : 'Save changes';
});

/**
 * n-drawer teleports its content to <body>, outside this component's root — so
 * a scoped `v-bind()` never reaches it, and the tinted group box stays inline.
 */
/**
 * The side panel's insets. It runs the full height beside the breadcrumb bar,
 * so its title starts below that bar (56px) plus the page's own top padding —
 * level with the page title, not the breadcrumbs. A form needs wider sides than
 * a rail's 16px.
 */
const panelStyle: CSSProperties = {
  paddingTop: `calc(56px + ${gaiaShellPadding.top})`,
  paddingLeft: '24px',
  paddingRight: '24px',
};

const groupBoxStyle: CSSProperties = {
  border: `1px solid ${gaiaNeutral.borderMuted}`,
  borderRadius: '10px',
  background: gaiaNeutral.surfaceSoft,
  padding: '14px',
};
</script>

<template>
  <!-- Create / edit in a right-hand drawer, so the list or record it edits
       stays in view beside it — or, `inline`, in a side panel the page makes
       room for, so the list beside it stays usable. -->
  <component
    :is="inline ? GaiaPanel : GaiaDrawer"
    v-bind="
      inline
        ? { style: panelStyle }
        : {
            show: showModel,
            'onUpdate:show': (value: boolean) => (showModel = value),
            width: config.formWidth,
            maskClosable: !saving,
            closeOnEsc: !saving,
          }
    "
  >
    <template #header>
      <!-- Title block, and in the panel its close button, top-aligned with the
           title rather than on the two-line block's baseline. -->
      <n-flex
        align="flex-start"
        :size="12"
        :wrap="false"
        :style="{ width: '100%', marginBottom: inline ? '10px' : 0 }"
      >
        <div :style="{ minWidth: 0 }">
          <gaia-text tag="div" size="subtitle" weight="semibold" tone="ink">
            {{ formTitle }}
          </gaia-text>
          <gaia-text
            tag="div"
            size="control"
            tone="subtle"
            :style="{ marginTop: '4px', lineHeight: 1.5 }"
          >
            {{ formSubtitle }}
          </gaia-text>
        </div>
        <!-- The overlay drawer brings its own close button; the panel doesn't. -->
        <gaia-icon-button
          v-if="inline"
          :dimension="28"
          quaternary
          title="Close"
          :style="{ marginLeft: 'auto', flex: 'none' }"
          @click="showModel = false"
        >
          <n-icon :component="IconClose" :size="14" />
        </gaia-icon-button>
      </n-flex>
    </template>
    <gaia-banner v-if="saveError" plain type="error" :style="{ marginBottom: '16px' }">
      {{ saveError }}
    </gaia-banner>

    <n-flex vertical :size="14">
      <template v-for="(group, index) in formGroups" :key="index">
        <!-- The toggle that opens the rarer name parts. -->
        <gaia-link
          v-if="group.kind === 'toggle'"
          :underline="false"
          class="more-toggle"
          @click="moreFieldsOpen = !moreFieldsOpen"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          >
            <path :d="moreFieldsOpen ? 'M2.5 7.5 6 4l3.5 3.5' : 'M2.5 4.5 6 8l3.5-3.5'" />
          </svg>
          {{ moreFieldsOpen ? config.collapsible?.expandedLabel : config.collapsible?.label }}
        </gaia-link>

        <gaia-divider
          v-else-if="group.kind === 'divider'"
          variant="subtle"
          :style="{ margin: 0 }"
        />

        <div
          v-else
          class="form-row"
          :style="[{ gridTemplateColumns: group.grid }, group.tinted ? groupBoxStyle : {}]"
        >
          <gaia-form-field
            v-for="field in group.fields"
            :key="field.key"
            :label="field.label"
            :required="field.required"
            :hint="field.hint"
            :error="formErrors[field.key] || undefined"
          >
            <entity-field-input
              :field="field"
              :value="formValues[field.key] ?? ''"
              :error="Boolean(formErrors[field.key])"
              :disabled="
                (field.createOnly && mode === 'edit') ||
                (mode === 'create' && lockedKeys.includes(field.key))
              "
              :seed-options="
                row ? (field.selectedOptions?.(row) ?? []) : (defaultOptions[field.key] ?? [])
              "
              @update:value="(value: string | string[]) => onFieldInput(field, value)"
            />
          </gaia-form-field>
        </div>
      </template>
    </n-flex>

    <!-- Access, in the same save: who else can see the new record. Folded by
         default — most records need nothing, and the note says who already
         sees it. -->
    <template v-if="canShare">
      <gaia-divider variant="subtle" :style="{ margin: '20px 0 16px' }" />
      <n-flex vertical :size="12">
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text variant="label" size="micro" tone="faint">Access</gaia-text>
          <gaia-link
            v-if="!shareOpen"
            :underline="false"
            :style="{ marginLeft: 'auto' }"
            @click="shareOpen = true"
          >
            Share with people or roles
          </gaia-link>
        </n-flex>
        <gaia-text size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
          Until it is shared, only you, admins and roles that can read every
          {{ label.toLowerCase() }} can see it.
        </gaia-text>
        <!-- Who, and at which level — one line, read as one sentence. -->
        <n-flex v-if="shareOpen" align="flex-start" :size="8" :wrap="false">
          <gaia-recipient-select
            v-model:users="shareUserUids"
            v-model:roles="shareRoleUids"
            :style="{ flex: 1, minWidth: 0 }"
          />
          <gaia-select
            v-model:value="shareLevel"
            :options="ACCESS_LEVEL_OPTIONS"
            aria-label="Access level"
            :style="{ width: '132px', flex: 'none' }"
          />
        </n-flex>
      </n-flex>
    </template>

    <template #footer>
      <n-flex align="center" :size="8" :wrap="false" :style="{ width: '100%' }">
        <gaia-text size="micro" tone="muted">{{ formFootNote }}</gaia-text>
        <gaia-button size="small" :style="{ marginLeft: 'auto' }" @click="showModel = false">
          Cancel
        </gaia-button>
        <gaia-button type="primary" size="small" :loading="saving" @click="saveForm">
          {{ submitLabel }}
        </gaia-button>
      </n-flex>
    </template>
  </component>
</template>

<style scoped>
/* Form fields sit on a per-row grid; each field's track comes from its config. */
.form-row {
  display: grid;
  gap: 12px;
}

/* Caret + label on one line; GaiaLink is an inline control by default. */
.more-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: fit-content;
}
</style>
